/**
 * Cart Notification Bar
 * Displays a notification when items are added to cart via AJAX
 */
(function() {
  'use strict';

  class CartNotificationBar {
    constructor() {
      this.bar = document.getElementById('cart-notification-bar');
      this.productTitleElement = this.bar?.querySelector('.cart-notification-bar__product-title');
      this.cartUpdateUnsubscriber = null;
      
      if (!this.bar) {
        console.warn('Cart notification bar element not found');
        return;
      }

      this.init();
    }

    init() {
      // Subscribe to cart update events from product-form.js
      if (typeof subscribe !== 'undefined' && typeof PUB_SUB_EVENTS !== 'undefined') {
        this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
          if (event.source === 'product-form' && event.cartData) {
            this.handleCartUpdate(event.cartData);
          }
        });
      }

      // Intercept fetch('/cart/add.js') calls
      this.interceptFetch();

      // ESC key to close (optional - user can still close manually if needed)
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && !this.bar.hasAttribute('hidden')) {
          this.hide();
        }
      });
    }

    interceptFetch() {
      const originalFetch = window.fetch;
      const self = this;

      window.fetch = function(...args) {
        const url = typeof args[0] === 'string' ? args[0] : args[0]?.url || '';
        
        // Check if this is a cart/add.js request
        if (url.includes('/cart/add.js') || url.includes(window.routes?.cart_add_url)) {
          return originalFetch.apply(this, args)
            .then(async (response) => {
              // Clone the response so we can read it without consuming it
              const clonedResponse = response.clone();
              
              // Only show notification for successful requests
              if (response.ok) {
                try {
                  const data = await clonedResponse.json();
                  // Check if it's not an error response
                  if (!data.status && !data.description) {
                    self.handleCartAddResponse(data);
                  }
                } catch (e) {
                  // If response is not JSON, it might be HTML (error page)
                  // Don't show notification in this case
                }
              }
              
              return response;
            })
            .catch((error) => {
              // Don't show notification on error
              throw error;
            });
        }
        
        // For all other fetch calls, use original fetch
        return originalFetch.apply(this, args);
      };
    }

    handleCartAddResponse(data) {
      // Extract product title from the response
      // Shopify cart/add.js response structure varies, try multiple approaches
      let productTitle = this.extractProductTitle(data);

      // Fallback: Try to get product title from the page
      if (!productTitle) {
        productTitle = this.getProductTitleFromPage();
      }

      if (productTitle) {
        this.show(productTitle);
      }
    }

    extractProductTitle(data) {
      if (!data) return null;

      // Handle items array (multiple items or cart response)
      if (data.items && Array.isArray(data.items) && data.items.length > 0) {
        const firstItem = data.items[0];
        let productTitle = firstItem.product_title || firstItem.title || firstItem.name || firstItem.product?.title;
        
        if (productTitle) {
          // If multiple items, show first item title with count
          if (data.items.length > 1) {
            productTitle += ` (+${data.items.length - 1} more)`;
          }
          return productTitle;
        }
      }

      // Try direct properties
      return data.product_title || data.title || data.name || data.variant_title || null;
    }

    getProductTitleFromPage() {
      // Try to find product title from common selectors
      const selectors = [
        'h1.product__title',
        'h1.product-title',
        '.product__title h1',
        '.product-title h1',
        '[data-product-title]',
        'product-info h1',
        'product-info .product__title'
      ];

      for (const selector of selectors) {
        const element = document.querySelector(selector);
        if (element) {
          return element.textContent.trim();
        }
      }

      return null;
    }

    handleCartUpdate(cartData) {
      // Extract product title from cartData
      // cartData from product-form.js is the response from /cart/add.js
      let productTitle = this.extractProductTitle(cartData);

      // Fallback: Try to get product title from the page
      if (!productTitle) {
        productTitle = this.getProductTitleFromPage();
      }

      if (productTitle) {
        this.show(productTitle);
      }
    }

    show(productTitle) {
      if (!this.bar || !this.productTitleElement) return;

      // Update product title
      this.productTitleElement.textContent = `"${productTitle}"`;

      // Show the bar
      this.bar.removeAttribute('hidden');
      this.bar.setAttribute('aria-hidden', 'false');
    }

    hide() {
      if (!this.bar) return;

      this.bar.setAttribute('aria-hidden', 'true');
      
      // Wait for animation to complete before hiding
      setTimeout(() => {
        this.bar.setAttribute('hidden', '');
      }, 300);
    }

    destroy() {
      if (this.cartUpdateUnsubscriber) {
        this.cartUpdateUnsubscriber();
      }
    }
  }

  // Initialize when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      new CartNotificationBar();
    });
  } else {
    new CartNotificationBar();
  }
})();

